import {
	collection, CollectionReference, deleteDoc, doc, DocumentData, Firestore, FirestoreDataConverter,
	Timestamp as FSTimestamp, getDoc, getDocs, getFirestore, query, QuerySnapshot, setDoc, updateDoc, where,
	writeBatch
} from 'firebase/firestore';
import _ from 'lodash';
import { app } from '../config/Firebase';
import { QuestionConfig, QuestionId, QuestionSetConfig, QuestionSetId, TimeStamp } from '../types/GameplayTypes';
import { Log } from '../util/Logging';

const db: Firestore = getFirestore(app);

// Typed collection references
function createTypedCollection<T = DocumentData>(collectionName: string): CollectionReference<T> {
	return collection(db, collectionName) as CollectionReference<T>;
}

/** Metadata as used at runtime within the app. Can be used with Redux. */
type AppMetadata<TId extends string> = {
	id: TId;
	createdAt: TimeStamp;
	updatedAt: TimeStamp;
};

/** How model objects are actually stored in the database. Ids are removed and Firestore Timestamp objects are used. */
type DatabaseModel<T extends AppMetadata<string>> = Omit<T, 'id' | 'createdAt' | 'updatedAt'> & {
	createdAt: FSTimestamp;
	updatedAt: FSTimestamp;
};

/** Create a converter to handle conversion between AppMetadata and DatabaseModel types */
export function metadataConverter<T extends AppMetadata<TId>, TId extends string>(): FirestoreDataConverter<T, DatabaseModel<T>> {
	return {
		toFirestore: (app: T): DatabaseModel<T> => ({
			..._.omit(app, 'id', 'createdAt', 'updatedAt'),
			createdAt: FSTimestamp.fromMillis(app.createdAt),
			updatedAt: FSTimestamp.now(),
		}),
		fromFirestore: (snapshot): T => ({
			...snapshot.data(),
			id: snapshot.id,
			createdAt: snapshot.data().createdAt.toMillis(),
			updatedAt: snapshot.data().updatedAt.toMillis(),
		} as T)
	};
}

export const Collections = {
	questionSets: createTypedCollection<QuestionSetConfig>('questionSets')
		.withConverter(metadataConverter<QuestionSetConfig, QuestionSetId>()),

	questions: createTypedCollection<QuestionConfig>('questions')
		.withConverter(metadataConverter<QuestionConfig, QuestionId>()),
};

// Generic CRUD operations for Firestore collections
function createCRUDOperations<T extends AppMetadata<TId>, TId extends `${Prefix}${number}`, Prefix extends string>(
	collectionRef: CollectionReference<T>,
	collectionName: string,
	idPrefix: Prefix,
) {
	async function nextId(): Promise<string> {
		const existingIds = (await getDocs(collectionRef)).docs.map(doc => doc.id);

		// Extract numeric parts and find the max
		const maxNum = existingIds
			.filter(id => id.startsWith(idPrefix))
			.map(id => parseInt(id.substring(idPrefix.length), 10))
			.filter(num => !isNaN(num))
			.reduce((max, num) => Math.max(max, num), 0);

		return `${idPrefix}${maxNum + 1}`;
	}

	return {
		async get(id: string): Promise<T> {
			Log.db.debug(`Fetching ${id} from ${collectionName}`);
			const snapshot = await getDoc(doc(collectionRef, id));

			if (!snapshot.exists()) {
				throw new Error(`${collectionName} "${id}" not found`);
			}

			Log.db.debug(`Fetched ${id} from ${collectionName}`);
			return snapshot.data();
		},

		async getAll(): Promise<T[]> {
			Log.db.debug(`Fetching all ${collectionName}`);
			const snapshot = await getDocs(collectionRef);
			Log.db.debug(`Fetched ${snapshot.size} ${collectionName}`);
			return snapshot.docs.map(doc => doc.data());
		},

		async create(data: Omit<T, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
			Log.db.info(`Creating ${collectionName}`, data);

			const id = await nextId();
			const now = FSTimestamp.now().toMillis();

			await setDoc(doc(collectionRef, id), {
				...data,
				id,
				createdAt: now,
				updatedAt: now,
			});

			Log.db.info(`Created ${collectionName} with ID: ${id}`);
			return id;
		},

		async update(id: string, updates: Partial<Omit<T, 'id' | 'createdAt' | 'authorName'>>): Promise<void> {
			Log.db.info(`Updating ${collectionName} ${id}`, updates);

			await updateDoc(doc(collectionRef, id), {
				...updates,
				updatedAt: FSTimestamp.now(),
			});

			Log.db.info(`Updated ${collectionName} ${id}`);
		},

		async delete(id: string): Promise<void> {
			Log.db.info(`Deleting ${collectionName} ${id}`);
			await deleteDoc(doc(collectionRef, id));
			Log.db.info(`Deleted ${collectionName} ${id}`);
		},
	};
}

// Question CRUD operations
export const QuestionOperations = {
	...createCRUDOperations<QuestionConfig, QuestionId, 'q'>(Collections.questions, 'question', 'q'),

	async getByTags(tags: string[]): Promise<QuestionConfig[]> {
		Log.db.debug('Fetching questions by tags', tags);
		const snapshot = await getDocs(query(Collections.questions, where('tags', 'array-contains-any', tags)));
		Log.db.debug(`Fetched ${snapshot.size} questions matching tags`);
		return snapshot.docs.map(doc => doc.data());
	},
};

// Question Set CRUD operations
export const QuestionSetOperations = {
	...createCRUDOperations<QuestionSetConfig, QuestionSetId, 'qset'>(Collections.questionSets, 'questionSet', 'qset'),

	async countSetsContainingQuestions(questionIds: QuestionId[]): Promise<number> {
		Log.db.debug('Counting Question Sets containing questions', questionIds);
		const sets = await QuestionSetOperations.setsWithQuestionIds(questionIds);
		Log.db.debug(`Found ${sets.size} Question Sets containing questions`);
		return sets.size;
	},

	async removeQuestionsFromAll(questionIds: QuestionId[]): Promise<void> {
		Log.db.info('Removing questions from all Question Sets', questionIds);
		const sets = await QuestionSetOperations.setsWithQuestionIds(questionIds);
		const batch = writeBatch(db);

		for (const docSnapshot of sets.docs) {
			const updatedQuestionIds = docSnapshot.data()
				.questionIds.filter(
					id => !questionIds.includes(id)
				);

			batch.update(doc(Collections.questionSets, docSnapshot.id), {
				questionIds: updatedQuestionIds,
				updatedAt: FSTimestamp.now()
			});
		}

		await batch.commit();
		Log.db.info(`Removed questions from ${sets.size} Question Sets`);
	},

	async setsWithQuestionIds(questionIds: `q${number}`[]): Promise<QuerySnapshot<QuestionSetConfig>> {
		return await getDocs(query(Collections.questionSets,
			where('questionIds', 'array-contains-any', questionIds)));
	},
};
